type LocaleOptions = {
  currency: string;
  locale?: string;
};

function processFormattedAmount(formattedAmount: string, options: LocaleOptions) {
  const { currency } = options;
  let [symbol, amount, notation] = formattedAmount.split(/([\d,.]+)/);

  if (currency === 'INR') {
    if (notation && notation.includes('T')) {
      notation = notation.replace('T', 'K'); // changes '₹300T' to '₹300k', '₹300TCr' to '₹300KCr'
    }
  }

  return `${symbol}${amount} ${notation}`.trim();
}

export function formatCurrency(
  amount: number = 0,
  options: LocaleOptions,
  formatOptions?: Intl.NumberFormatOptions
) {
  const { currency, locale } = options;

  const defaultLocale = currencyToLocaleMapping[currency] ?? 'en-US';

  const formattedAmount = new Intl.NumberFormat(locale ?? defaultLocale, {
    style: 'currency',
    currency: currency ?? 'USD',
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 2,
    ...formatOptions,
  }).format(amount);

  return processFormattedAmount(formattedAmount, options);
}

const currencyToLocaleMapping: Record<string, string> = {
  INR: 'en-IN',
};

export function getCurrencySymbol(currency: string = 'USD') {
  // tries to format "1" and return the first element of the string (viz. currency symbol)
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).format(1)[0];
  } catch (e) {
    return '$';
  }
}

export function getPercentage(numerator: number, denominator: number = 1) {
  if (denominator === 0) return new Intl.NumberFormat('en-US', { style: 'percent' }).format(0);
  return new Intl.NumberFormat('en-US', { style: 'percent' }).format(numerator / denominator);
}

export function formatPercent(percent: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(percent / 100);
}

export function formatAmountShort(amount: number | string, currency: string) {
  return formatCurrency(
    Number(amount),
    { currency },
    { compactDisplay: 'short', notation: 'compact' }
  );
}
